<template>
  <div class="add-edit-certificates uk-grid-medium" uk-grid>
    <div class="uk-width-5-5">
      <div class="d-flex">
        <nav id="breadcrumbs" class="mb-3">
          <ul>
            <li>
              <router-link :to="{ name: 'company.dashboard' }">
                <i class="uil-home-alt"></i>
              </router-link>
            </li>
            <li>{{ $t("certificate.management") }}</li>
            <li>{{ isEditMode ? $t("certificate.edit") : $t("certificate.add") }}</li>
          </ul>
        </nav>
      </div>

      <template v-if="errorMessages">
        <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p style="color: red">
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </template>
      <div v-if="successMessage" class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: green">
          <i class="uil-exclamation-triangle"></i> {{ successMessage }}
        </p>
      </div>
      <div class="uk-grid" uk-grid="">
        <CertificateTabNavigation :activeTab="activeTab" @changeTab="changeTab"/>

        <div class="uk-width-expand">
          <ul id="user-group-tab" class="uk-switcher uk-margin" style="touch-action: pan-y pinch-zoom">
            <li>
              <CertificateFeaturesForm :certificateTypeOptions="certificateTypeOptions"
                                       :formData="formData"
                                       :selectedCertificateTypes="selectedCertificateTypes"
                                       @goToNextTab="goToNextTab"
                                       @handleCertificateTypeTag="handleCertificateTypeTag"
                                       @update:formData="formData = $event"
                                       @update:selectedCertificateTypes="selectedCertificateTypes = $event"
              />
              <div class="uk-margin-small-top">
                <CertificateTemplatePreview :company="company"
                                            :coverImageLink="cover_image_link"
                                            :formData="formData"
                                            :isEditMode="isEditMode"
                                            :templates="templates"
                                            @changeCoverPicture="changeCoverPicture"
                                            @saveForm="saveForm"
                                            @update:formData="formData = $event"
                />
              </div>
            </li>
            <li>
              <CertificateTypesManagement @typesUpdated="getCertificateTypes"/>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/core/services";
import moment from "moment";
import module, {
  BASE_URL,
  CREATE_ITEM,
  GET_ITEM,
  UPDATE_ITEM,
  ITEM,
  MODULE_NAME,
  ERROR,
} from "@/core/services/store/certificate.module";

import template_module, {
  BASE_URL as TEMPLATE_BASE_URL,
  GET_ITEMS as TEMPLATE_GET_ITEMS,
  ITEMS as TEMPLATE_ITEMS,
  MODULE_NAME as TEMPLATE_MODULE_NAME,
} from "@/core/services/store/certificate_template.module";

import company_module, {
  BASE_URL as COMPANY_BASE_URL,
  GET_ITEM as COMPANY_GET_ITEMS,
  ITEM as COMPANY_ITEM,
  MODULE_NAME as COMPANY_MODULE_NAME,
} from "@/core/services/store/company.module";

import certificateTypeMixin from "./certificateTypeMixin";
import CertificateTabNavigation from "./components/CertificateTabNavigation";
import CertificateFeaturesForm from "./components/CertificateFeaturesForm";
import CertificateTemplatePreview from "./components/CertificateTemplatePreview";
import CertificateTypesManagement from "./components/CertificateTypesManagement";

export default {
  name: "AddEditCertificates",
  components: {
    CertificateTabNavigation,
    CertificateFeaturesForm,
    CertificateTemplatePreview,
    CertificateTypesManagement
  },
  mixins: [certificateTypeMixin],
  data() {
    return {
      activeTab: 0,
      cover_image_link: null,
      successMessage: null,
      errorMessages: [],
      formData: {
        name: null,
        certifying_name: "",
        certifying_title: null,
        certificate_template_id: 1,
        text: this.$t("certificate.body_text_example"),
        expireable: false,
        end_date: moment().format("YYYY-MM-DD"),
        renewal_type_id: "1",
        shareable: false,
        mail_notify: false,
        send_expiry_reminder_day: null,
        status_id: true,
      },
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
    registerStoreModule(TEMPLATE_MODULE_NAME, template_module);
    registerStoreModule(COMPANY_MODULE_NAME, company_module);
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id;
    },
    templates() {
      return store.getters[TEMPLATE_MODULE_NAME + "/" + TEMPLATE_ITEMS];
    },
    certificate() {
      return store.getters[MODULE_NAME + "/" + ITEM];
    },
    company() {
      return store.getters[COMPANY_MODULE_NAME + "/" + COMPANY_ITEM];
    },
    error() {
      return store.getters[MODULE_NAME + "/" + ERROR];
    }
  },
  methods: {
    getTemplateItems() {
      this.$store.dispatch(TEMPLATE_MODULE_NAME + "/" + TEMPLATE_GET_ITEMS, {
        url: TEMPLATE_BASE_URL,
        filters: {
          order: "asc",
          sort: "id",
          status_id: 1,
        },
      });
    },
    getCompanyItem() {
      this.$store.dispatch(COMPANY_MODULE_NAME + "/" + COMPANY_GET_ITEMS, {
        url: COMPANY_BASE_URL + "/self",
      });
    },
    getCertificateItem() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEM, {
        url: BASE_URL + "/" + this.$route.params.id,
      });
    },
    changeCoverPicture() {
      if (!this.formData.certificate_template_id) {
        this.cover_image_link = null;
        return false;
      }
      let pos = this.templates
          .map(function (x) {
            return x.id;
          })
          .indexOf(this.formData.certificate_template_id);
      if (pos !== -1) {
        this.cover_image_link = this.templates[pos]["cover_image_link"];
      }
    },
    saveForm() {
      let self = this.formData;
      this.errorMessages = [];

      if (!self.name || self.name.length < 2) {
        this.scrollToTop();
        this.errorMessages.push(
            this.$t("validation.required_field").replace(
                ":NAME",
                this.$t("certificate.name")
            )
        );
        return false;
      }
      if (self.text < 2) {
        this.scrollToTop();
        this.errorMessages.push(
            this.$t("validation.required_field").replace(
                ":NAME",
                this.$t("certificate.text")
            )
        );
        return false;
      }
      if (!self.text.includes("{egitim.adi}")) {
        this.scrollToTop();
        this.errorMessages.push(
            this.$t("validation.required_field").replace(
                ":NAME",
                this.$t("certificate.text") + " {egitim.adi}"
            )
        );
        return false;
      }
      if (this.isEditMode && !self.certificate_template_id) {
        this.scrollToTop();
        this.errorMessages.push(
            this.$t("validation.required_field").replace(
                ":NAME",
                this.$t("general.cetificate_template")
            )
        );
        return false;
      }

      let reminderDay = null;
      if (self.expireable) {
        const parsedReminderDay = parseInt(self.send_expiry_reminder_day, 10);
        reminderDay = Number.isInteger(parsedReminderDay) && parsedReminderDay > 0 ? parsedReminderDay : null;
      }

      if (this.isEditMode) {
        this.updateCertificate(self, reminderDay);
      } else {
        this.createCertificate(self, reminderDay);
      }
    },
    createCertificate(self, reminderDay) {
      let formData = new FormData();
      formData.append("name", self.name);
      formData.append("certificate_template_id", self.certificate_template_id);
      formData.append("certifying_name", self.certifying_name);
      formData.append("certifying_title", self.certifying_title);
      formData.append("text", self.text);
      formData.append("end_date", !self.expireable ? null : self.end_date);
      formData.append("renewal_type_id", self.renewal_type_id);
      formData.append("expireable", self.expireable ? "1" : "0");
      formData.append("shareable", self.shareable ? "1" : "0");
      formData.append("mail_notify", self.mail_notify ? "1" : "0");
      formData.append("send_expiry_reminder_day", reminderDay);
      formData.append("status_id", self.status_id ? "1" : "0");
      if (this.selectedCertificateTypes && this.selectedCertificateTypes.length) {
        this.selectedCertificateTypes.forEach((item) => {
          formData.append("type_ids[]", item.id);
        });
      }
      store
          .dispatch(MODULE_NAME + "/" + CREATE_ITEM, {
            url: BASE_URL,
            contents: formData,
          })
          .then(() => {
            this.scrollToTop();
            if (!this.error) {
              this.successMessage = this.$t("general.successfully_created");
              this.$router.push({ name: "company.certificate.list" });
            } else this.errorMessages.push(this.error);
          });
    },
    updateCertificate(self, reminderDay) {
      let contents = {
        name: self.name,
        certificate_template_id: self.certificate_template_id,
        certifying_name: self.certifying_name,
        certifying_title: self.certifying_title,
        text: self.text,
        end_date: !self.expireable ? null : (self.end_date ? moment(self.end_date).format("YYYY-MM-DD") : null),
        renewal_type_id: self.renewal_type_id,
        expireable: self.expireable ? 1 : 0,
        shareable: self.shareable ? "1" : "0",
        mail_notify: self.mail_notify ? "1" : "0",
        send_expiry_reminder_day: reminderDay,
        status_id: self.status_id ? "1" : "0",
        type_ids: this.selectedCertificateTypes.map(item => item.id),
      };
      store
          .dispatch(MODULE_NAME + "/" + UPDATE_ITEM, {
            url: BASE_URL + "/" + this.$route.params.id,
            contents: contents,
          })
          .then(() => {
            this.scrollToTop();
            if (!this.error) {
              this.successMessage = this.$t("general.successfully_updated");
            } else this.errorMessages.push(this.error);
          });
    },
    populateFormFromCertificate(cert) {
      if (!cert) return;
      this.formData.name = cert.name;
      this.formData.certifying_name = cert.certifying_name;
      this.formData.certifying_title = cert.certifying_title;
      this.formData.certificate_template_id = cert.certificate_template_id;
      this.formData.text = cert.text;
      this.formData.expireable = cert.expireable === 1 || cert.expireable === "1" || cert.expireable === true;
      this.formData.end_date = cert.end_date ? moment(cert.end_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
      this.formData.renewal_type_id = cert.renewal_type_id;
      this.formData.shareable = !!cert.shareable;
      this.formData.mail_notify = !!cert.mail_notify;
      this.formData.send_expiry_reminder_day = cert.send_expiry_reminder_day;
      this.formData.status_id = !!cert.status_id;
    },
    resetMessages() {
      this.errorMessages = [];
      this.successMessage = null;
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
    goToNextTab() {
      if (this.activeTab < 2) {
        this.activeTab++;
        this.changeTab(this.activeTab);
      }
    },
    changeTab(index) {
      this.activeTab = index;
      this.scrollToTop();
      this.$nextTick(() => {
        if (window.UIkit) {
          const navElement = document.querySelector('#certificate-tab-nav');
          if (navElement) {
            const tabItems = navElement.querySelectorAll('li');
            tabItems.forEach((item, i) => {
              if (i === index) {
                item.classList.add('uk-active');
              } else {
                item.classList.remove('uk-active');
              }
            });

            const switcherElement = document.querySelector('#user-group-tab');
            if (switcherElement) {
              const switcherItems = switcherElement.querySelectorAll(':scope > *');
              switcherItems.forEach((item, i) => {
                if (i === index) {
                  item.classList.add('uk-active');
                  item.removeAttribute('hidden');
                } else {
                  item.classList.remove('uk-active');
                  item.setAttribute('hidden', '');
                }
              });
            }
          }
        }
      });
    },
  },
  mounted() {
    this.getTemplateItems();
    this.getCompanyItem();
    this.getCertificateTypes();
    if (this.isEditMode) {
      this.getCertificateItem();
    }
  },
  watch: {
    '$i18n.locale'(newLocale) {
      if (!this.isEditMode) {
        this.formData.text = this.$t('certificate.body_text_example');
      }
    },
    certificate: {
      handler(val) {
        if (this.isEditMode && val) {
          this.populateFormFromCertificate(val);
        }
      },
      immediate: true,
    },
    "certificate.types": {
      handler(val) {
        if (this.isEditMode && Array.isArray(val)) {
          this.selectedCertificateTypes = val.map(item => ({ id: item.id, name: item.name }));
        }
      },
      immediate: true,
    },
    successMessage: function (val) {
      this.debouncedResetValues();
    },
    errorMessages: function (val) {
      this.debouncedResetValues();
    },
    "formData.certificate_template_id": function (val) {
      this.changeCoverPicture();
    },
    templates: function (val) {
      if (val && val.length && this.formData.certificate_template_id) {
        this.changeCoverPicture();
      }
    },
  },
  created: function () {
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>
<style scoped>
input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}
</style>
